/**
 * ManagePhotosSheet — FE-212.
 *
 * Owner-only modal for managing the photos on an item. Lets the
 * user:
 *
 *   - Add up to MAX_USER_PHOTOS (4) USER_UPLOAD photos
 *   - Delete a specific user-added photo
 *   - Reorder user-added photos (↑ ↓ arrows — drag-and-drop on
 *     React-Native-Web is fiddly and the spec accepts arrows as
 *     the fallback)
 *
 * Backend constraints baked in:
 *   - Position 0 (PRIMARY_AUTO) is non-deletable, non-reorderable
 *   - Reorder PATCH sends only user-added photo_ids
 *   - Upload PATCH accepts up to 4 files per call AND respects the
 *     total cap server-side (returns 413 if exceeded)
 *
 * Wire-up:
 *   - Parent passes the current `photos[]` array + item id + the
 *     `viewerIsOwner` flag (non-owner sheet is unreachable since
 *     the affordance is hidden, but we guard regardless)
 *   - On mutate, parent's `onPhotosChanged(newPhotos)` callback
 *     fires with the canonical new list — parent merges into its
 *     own ItemDetail/inventory state
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Image,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import Toast from 'react-native-toast-message';
import { fpColors } from '../../../styles/theme';
import { api } from '../../../api/remembrApi';
import { MAX_USER_PHOTOS } from '../../../api/types';
import type { PhotoSummary } from '../../../api/types';

export interface ManagePhotosSheetProps {
  visible: boolean;
  itemId: number;
  photos: PhotoSummary[];
  viewerIsOwner: boolean;
  onClose: () => void;
  /** Called with the canonical new photo list after any successful
   *  mutate (upload / delete / reorder). Parent merges into the
   *  ItemDetail / inventory slice. */
  onPhotosChanged: (next: PhotoSummary[]) => void;
}

export const ManagePhotosSheet: React.FC<ManagePhotosSheetProps> = ({
  visible,
  itemId,
  photos,
  viewerIsOwner,
  onClose,
  onPhotosChanged,
}) => {
  // Local working copy so reorder can be applied incrementally
  // before we PATCH on close (or on an explicit Save). Keeps the
  // tile grid responsive without waiting for the server on each
  // arrow tap.
  // FE-249.1 — defensive: coerce non-array props to []. Stale route
  // params (`?item=%5Bobject%20Object%5D` from a bad navigate call)
  // can land us on this screen with `item` as a string instead of an
  // object, which would route a non-array through here and crash
  // `.find` on the first render after the sheet opens.
  const [workingSet, setWorkingSet] = useState<PhotoSummary[]>(
    Array.isArray(photos) ? photos : [],
  );
  const [busy, setBusy] = useState<null | 'upload' | 'delete' | 'reorder'>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Sprint 8 hotfix — moved up from below the early return on line ~95.
  // Calling useRef AFTER the `if (!visible) return null` (when visible
  // was false on first render, true on second) violated the Rules of
  // Hooks: React saw N hooks on the closed render and N+1 on the open
  // render, then tripped error #310 ("Rendered fewer hooks than
  // expected.") and the ErrorBoundary nuked the whole ItemDetail page.
  // Every hook must execute on every render — the early return now
  // happens AFTER all hooks are declared.
  const reorderDirty = useRef(false);

  // Reset working set on every open so re-opens don't carry stale
  // optimistic reorders. We sort by display_order ONCE on each fresh
  // open so the canonical server order seeds the local state; from
  // then on, moveUp / moveDown maintain the order via in-place swaps
  // and we trust workingSet's array order (don't re-sort in render,
  // that would erase the swap because display_order doesn't update
  // until the PATCH on close).
  useEffect(() => {
    if (visible) {
      // FE-249.1 — same defensive coercion: never spread a non-array.
      const safe = Array.isArray(photos) ? photos : [];
      const seed = [...safe].sort(
        (a, b) => a.display_order - b.display_order,
      );
      setWorkingSet(seed);
      setError(null);
      setBusy(null);
      // FE-249.4 — refetch the canonical photos[] on every open.
      // The prop is whatever the parent had cached, which can be:
      //   - stale (item was loaded from a list endpoint that strips
      //     photos[], so the prop is [])
      //   - missing the PRIMARY_AUTO row (legacy items pre BE-235
      //     backfill)
      //   - out of sync after a mutation from another tab / session
      // Refetching guarantees the user always sees the truth on the
      // server when they open the modal. Best-effort: a network
      // error leaves the prop-seeded state in place.
      //
      // NB: we intentionally do NOT call onPhotosChanged here. The
      // parent ItemDetailScreen has its own listItemPhotos effect
      // (FE-249.4) that mirrors photos[] into item state — calling
      // back would re-trigger this effect (parent re-render →
      // new photos prop → effect deps change → refetch loop).
      (async () => {
        try {
          const fresh = await api.listItemPhotos(itemId);
          if (Array.isArray(fresh)) {
            setWorkingSet(
              [...fresh].sort(
                (a, b) => a.display_order - b.display_order,
              ),
            );
          }
        } catch {
          /* swallow — prop seed is still in workingSet */
        }
      })();
    }
    // Intentionally omit `photos` + `onPhotosChanged` from deps to
    // avoid re-firing when the parent updates them; the open trigger
    // is `visible` flipping true.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, itemId]);

  if (!visible) return null;

  // Split into the invariant primary + the editable user-added rows.
  // No `.sort()` here on purpose — see the useEffect note above.
  // FE-249.1 — guard against workingSet being non-array (e.g. server
  // returned an error envelope from the post-mutation refetch instead
  // of a PhotoSummary[]). Fall back to empty so render doesn't crash.
  const ws = Array.isArray(workingSet) ? workingSet : [];
  const primary = ws.find((p) => p.source === 'PRIMARY_AUTO') ?? null;
  const userAdded = ws.filter((p) => p.source !== 'PRIMARY_AUTO');

  const remainingSlots = MAX_USER_PHOTOS - userAdded.length;
  const canAdd = remainingSlots > 0 && busy == null && viewerIsOwner;

  // ── Upload ────────────────────────────────────────────────────────
  const handleAddPress = () => {
    if (!canAdd) return;
    if (Platform.OS === 'web') {
      // Re-use a single input element across opens so the file
      // dialog doesn't pile up DOM nodes.
      if (!fileInputRef.current) {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.multiple = true;
        input.style.display = 'none';
        input.addEventListener('change', async () => {
          const fileList = input.files;
          if (!fileList || fileList.length === 0) return;
          await uploadFiles(Array.from(fileList).slice(0, remainingSlots));
          // Reset value so picking the same file twice still fires.
          input.value = '';
        });
        document.body.appendChild(input);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore — RN Web only
        fileInputRef.current = input;
      }
      fileInputRef.current.click();
      return;
    }
    Toast.show({
      type: 'info',
      text1: 'Photo upload is web-only for now',
      text2: 'Mobile camera support coming soon.',
    });
  };

  const uploadFiles = async (files: File[]) => {
    if (files.length === 0) return;
    setBusy('upload');
    setError(null);
    try {
      const payload = files.map((f) => ({
        uri: URL.createObjectURL(f),
        name: f.name,
        type: f.type || 'image/jpeg',
      }));
      const newRows = await api.uploadItemPhotos(itemId, payload);
      // FE-249 — refetch the canonical photo list after upload.
      // Building `next` by merging client-side fails when the original
      // `photos` prop was empty (legacy items where the hero is served
      // out of `primary_photo_url` / fallbackUrl, with no PRIMARY_AUTO
      // row in photos[]). In that case the merge dropped the implicit
      // primary, and the carousel re-rendered with ONLY the new user
      // uploads — the hero "disappeared". Refetching via
      // listItemPhotos returns the full server-authoritative list
      // (PRIMARY_AUTO + all user uploads, in display order) so we
      // never have to guess which rows existed before this call.
      // FE-249.1 — validate the refetch shape. If the server returned
      // a non-array (error envelope, wrapper object), fall back to the
      // local merge rather than poisoning workingSet.
      const safeNewRows: PhotoSummary[] = Array.isArray(newRows) ? newRows : [];
      let next: PhotoSummary[];
      try {
        const fresh = await api.listItemPhotos(itemId);
        next = Array.isArray(fresh)
          ? fresh
          : [
              ...(primary ? [primary] : []),
              ...userAdded,
              ...safeNewRows,
            ];
      } catch {
        // Fallback to local merge if the refetch fails — at worst the
        // user sees an incomplete carousel until the next refresh, but
        // the upload still landed and the next page load will recover.
        next = [
          ...(primary ? [primary] : []),
          ...userAdded,
          ...safeNewRows,
        ];
      }
      setWorkingSet(next);
      onPhotosChanged(next);
      Toast.show({
        type: 'success',
        text1: safeNewRows.length === 1
          ? 'Photo added'
          : `${safeNewRows.length} photos added`,
      });
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        'Upload failed — please try again.';
      // Surface 413 + 422 with friendly copy.
      if (err?.response?.status === 413) {
        setError(
          `Each item can have at most ${MAX_USER_PHOTOS} additional photos.`,
        );
      } else {
        setError(typeof detail === 'string' ? detail : 'Upload failed.');
      }
    } finally {
      setBusy(null);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────
  const handleDeletePress = (photo: PhotoSummary) => {
    if (busy) return;
    if (photo.source === 'PRIMARY_AUTO') {
      // Defensive — the UI doesn't expose the button on the primary
      // row, but in case it ever does, refuse to fire.
      Toast.show({
        type: 'info',
        text1: 'The primary photo is auto-generated',
        text2: 'It can\'t be deleted manually.',
      });
      return;
    }
    const confirm = () =>
      runDelete(photo).catch(() => {
        /* error already toasted */
      });
    if (Platform.OS === 'web') {
      // Alert.alert on web doesn't show buttons; use window.confirm.
      // eslint-disable-next-line no-alert
      if (window.confirm('Delete this photo?')) confirm();
    } else {
      Alert.alert(
        'Delete this photo?',
        'This cannot be undone.',
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Delete', style: 'destructive', onPress: confirm },
        ],
      );
    }
  };

  const runDelete = async (photo: PhotoSummary) => {
    setBusy('delete');
    setError(null);
    try {
      await api.deleteItemPhoto(itemId, photo.id);
      // FE-249 — same refetch pattern as upload. Filtering workingSet
      // locally is correct most of the time, but if workingSet was
      // missing a PRIMARY_AUTO row (legacy items), we'd never recover
      // it — refetching guarantees we sync to server truth.
      // FE-249.1 — validate the refetch returned an array; if not,
      // fall back to the local filter rather than letting a non-array
      // poison workingSet (which crashes line ~120's `.find`).
      let next: PhotoSummary[];
      try {
        const fresh = await api.listItemPhotos(itemId);
        next = Array.isArray(fresh)
          ? fresh
          : ws.filter((p) => p.id !== photo.id);
      } catch {
        next = ws.filter((p) => p.id !== photo.id);
      }
      setWorkingSet(next);
      onPhotosChanged(next);
      Toast.show({ type: 'success', text1: 'Photo deleted' });
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          'Could not delete — try again.',
      );
    } finally {
      setBusy(null);
    }
  };

  // ── Reorder ───────────────────────────────────────────────────────
  // Up/Down arrows are inline on each tile. Optimistic local swap +
  // a PATCH fired on the way out closes the loop with the server.
  // We debounce the PATCH to the close handler so rapid taps don't
  // generate N round-trips.
  // (reorderDirty is declared above the early return — see hotfix
  // comment near useState block.)

  const moveUp = (id: number) => {
    setWorkingSet((prev) => {
      const prim = prev.find((p) => p.source === 'PRIMARY_AUTO') ?? null;
      const ua = prev.filter((p) => p.source !== 'PRIMARY_AUTO');
      const idx = ua.findIndex((p) => p.id === id);
      if (idx <= 0) return prev;
      const swapped = [...ua];
      [swapped[idx - 1], swapped[idx]] = [swapped[idx], swapped[idx - 1]];
      reorderDirty.current = true;
      return [...(prim ? [prim] : []), ...swapped];
    });
  };

  const moveDown = (id: number) => {
    setWorkingSet((prev) => {
      const prim = prev.find((p) => p.source === 'PRIMARY_AUTO') ?? null;
      const ua = prev.filter((p) => p.source !== 'PRIMARY_AUTO');
      const idx = ua.findIndex((p) => p.id === id);
      if (idx < 0 || idx >= ua.length - 1) return prev;
      const swapped = [...ua];
      [swapped[idx], swapped[idx + 1]] = [swapped[idx + 1], swapped[idx]];
      reorderDirty.current = true;
      return [...(prim ? [prim] : []), ...swapped];
    });
  };

  const handleClose = async () => {
    // Flush any pending reorder before tearing down.
    if (reorderDirty.current && userAdded.length > 0 && busy == null) {
      setBusy('reorder');
      try {
        const ids = userAdded.map((p) => p.id);
        const fresh = await api.reorderItemPhotos(itemId, ids);
        // fresh is just the user-added rows in canonical order.
        // FE-249.1 — defensive: if BE returned a non-array, fall back
        // to the local in-memory order rather than corrupting state.
        const safeFresh: PhotoSummary[] = Array.isArray(fresh)
          ? fresh
          : userAdded;
        const next = [...(primary ? [primary] : []), ...safeFresh];
        onPhotosChanged(next);
        reorderDirty.current = false;
      } catch (err: any) {
        // Reorder failure shouldn't block closing — toast + bail.
        Toast.show({
          type: 'error',
          text1: 'Could not save new order',
          text2:
            err?.response?.data?.detail ||
            err?.message ||
            'The previous order has been kept.',
        });
      } finally {
        setBusy(null);
      }
    }
    onClose();
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      onRequestClose={handleClose}
    >
      <View style={styles.overlay}>
        <View style={styles.sheet}>
          <View style={styles.header}>
            <Text style={styles.title}>Manage photos</Text>
            <Pressable
              onPress={handleClose}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel="Close"
              testID="manage-photos-close"
            >
              <MaterialIcons name="close" size={22} color={fpColors.onSurface} />
            </Pressable>
          </View>
          <Text style={styles.counter} testID="manage-photos-counter">
            {userAdded.length} of {MAX_USER_PHOTOS} additional photos
            {primary ? ' · 1 auto photo' : ''}
          </Text>

          {error ? (
            <View style={styles.errorBanner}>
              <MaterialIcons
                name="error-outline"
                size={16}
                color={fpColors.error}
              />
              <Text style={styles.errorText} numberOfLines={2}>
                {error}
              </Text>
            </View>
          ) : null}

          <ScrollView
            style={styles.grid}
            contentContainerStyle={styles.gridContent}
          >
            {primary ? (
              <View
                style={[styles.tile, styles.tilePrimary]}
                testID={`manage-photos-tile-primary`}
              >
                <Image
                  source={{ uri: primary.url }}
                  style={styles.tileImage}
                  accessibilityIgnoresInvertColors
                />
                <View style={styles.tilePrimaryBadge}>
                  <MaterialIcons
                    name="auto-awesome"
                    size={11}
                    color={fpColors.onPrimary}
                  />
                  <Text style={styles.tilePrimaryBadgeText}>
                    Auto · pinned
                  </Text>
                </View>
              </View>
            ) : null}

            {userAdded.map((p, i) => (
              <View
                style={styles.tile}
                key={`manage-tile-${p.id}`}
                testID={`manage-photos-tile-${p.id}`}
              >
                <Image
                  source={{ uri: p.url }}
                  style={styles.tileImage}
                  accessibilityIgnoresInvertColors
                />
                <View style={styles.tileBadge}>
                  <Text style={styles.tileBadgeText}>{i + 2}</Text>
                </View>
                <View style={styles.tileActions}>
                  <Pressable
                    onPress={() => moveUp(p.id)}
                    disabled={i === 0 || busy != null}
                    style={({ pressed }) => [
                      styles.tileActionBtn,
                      (i === 0 || busy != null) && styles.tileActionBtnDisabled,
                      pressed && { opacity: 0.85 },
                    ]}
                    accessibilityRole="button"
                    accessibilityLabel="Move up"
                    accessibilityState={{ disabled: i === 0 }}
                    testID={`manage-photos-up-${p.id}`}
                  >
                    <MaterialIcons
                      name="arrow-upward"
                      size={14}
                      color={fpColors.onSurface}
                    />
                  </Pressable>
                  <Pressable
                    onPress={() => moveDown(p.id)}
                    disabled={i === userAdded.length - 1 || busy != null}
                    style={({ pressed }) => [
                      styles.tileActionBtn,
                      (i === userAdded.length - 1 || busy != null) &&
                        styles.tileActionBtnDisabled,
                      pressed && { opacity: 0.85 },
                    ]}
                    accessibilityRole="button"
                    accessibilityLabel="Move down"
                    accessibilityState={{
                      disabled: i === userAdded.length - 1,
                    }}
                    testID={`manage-photos-down-${p.id}`}
                  >
                    <MaterialIcons
                      name="arrow-downward"
                      size={14}
                      color={fpColors.onSurface}
                    />
                  </Pressable>
                  <Pressable
                    onPress={() => handleDeletePress(p)}
                    disabled={busy != null}
                    style={({ pressed }) => [
                      styles.tileActionBtn,
                      styles.tileActionBtnDanger,
                      busy != null && styles.tileActionBtnDisabled,
                      pressed && { opacity: 0.85 },
                    ]}
                    accessibilityRole="button"
                    accessibilityLabel="Delete this photo"
                    testID={`manage-photos-delete-${p.id}`}
                  >
                    <MaterialIcons
                      name="delete-outline"
                      size={14}
                      color={fpColors.error}
                    />
                  </Pressable>
                </View>
              </View>
            ))}

            {/* Add tile — disabled at cap. */}
            <Pressable
              onPress={handleAddPress}
              disabled={!canAdd}
              style={({ pressed }) => [
                styles.tile,
                styles.tileAdd,
                !canAdd && styles.tileAddDisabled,
                pressed && canAdd && { opacity: 0.85 },
              ]}
              accessibilityRole="button"
              accessibilityLabel={
                canAdd
                  ? 'Add a photo'
                  : `Maximum ${MAX_USER_PHOTOS} additional photos`
              }
              accessibilityState={{ disabled: !canAdd }}
              testID="manage-photos-add-tile"
            >
              {busy === 'upload' ? (
                <ActivityIndicator color={fpColors.primary} />
              ) : (
                <>
                  <MaterialIcons
                    name={canAdd ? 'add-a-photo' : 'photo-library'}
                    size={28}
                    color={canAdd ? fpColors.primary : fpColors.outline}
                  />
                  <Text
                    style={[
                      styles.tileAddText,
                      !canAdd && { color: fpColors.outline },
                    ]}
                  >
                    {canAdd ? 'Add a photo' : 'Max reached'}
                  </Text>
                </>
              )}
            </Pressable>
          </ScrollView>

          <View style={styles.footer}>
            <Pressable
              onPress={handleClose}
              style={({ pressed }) => [
                styles.doneBtn,
                pressed && { opacity: 0.9 },
              ]}
              disabled={busy === 'reorder'}
              accessibilityRole="button"
              accessibilityLabel="Done"
              testID="manage-photos-done"
            >
              {busy === 'reorder' ? (
                <ActivityIndicator color={fpColors.onPrimary} />
              ) : (
                <Text style={styles.doneBtnText}>Done</Text>
              )}
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const TILE_SIZE = 100;

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  sheet: {
    width: '100%',
    maxWidth: 560,
    maxHeight: '90%',
    backgroundColor: fpColors.surface,
    borderRadius: 16,
    padding: 20,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  title: {
    fontFamily: 'Inter_700Bold',
    fontSize: 18,
    color: fpColors.onSurface,
  },
  counter: {
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    marginBottom: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 10,
    backgroundColor: 'rgba(186,26,26,0.08)',
    borderRadius: 8,
    marginBottom: 10,
  },
  errorText: {
    flex: 1,
    fontSize: 12,
    color: fpColors.error,
  },
  grid: {
    flexGrow: 0,
    maxHeight: 360,
  },
  gridContent: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    paddingBottom: 4,
  },
  tile: {
    width: TILE_SIZE,
    height: TILE_SIZE,
    borderRadius: 10,
    overflow: 'hidden',
    backgroundColor: fpColors.surfaceContainerLow,
    position: 'relative',
  },
  tilePrimary: {
    borderWidth: 2,
    borderColor: fpColors.primary,
  },
  tileImage: {
    width: '100%',
    height: '100%',
  },
  tilePrimaryBadge: {
    position: 'absolute',
    bottom: 4,
    left: 4,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
    backgroundColor: fpColors.primary,
  },
  tilePrimaryBadgeText: {
    fontSize: 9,
    fontWeight: '700',
    color: fpColors.onPrimary,
  },
  tileBadge: {
    position: 'absolute',
    top: 4,
    left: 4,
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tileBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#fff',
  },
  tileActions: {
    position: 'absolute',
    bottom: 4,
    right: 4,
    flexDirection: 'row',
    gap: 3,
  },
  tileActionBtn: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tileActionBtnDanger: {
    backgroundColor: 'rgba(255, 240, 240, 0.95)',
  },
  tileActionBtnDisabled: {
    opacity: 0.35,
  },
  tileAdd: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1.5,
    borderColor: fpColors.outlineVariant,
    borderStyle: 'dashed',
  },
  tileAddDisabled: {
    opacity: 0.55,
  },
  tileAddText: {
    fontSize: 11,
    fontWeight: '600',
    color: fpColors.primary,
  },
  footer: {
    marginTop: 14,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  doneBtn: {
    minWidth: 100,
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: fpColors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  doneBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: fpColors.onPrimary,
  },
});
